/**
 * Which control scheme to *present*.
 *
 * This decides what the player sees — whether the on-screen sticks are shown — not which
 * producer is attached. Both producers stay attached at all times, because devices with
 * both a touchscreen and a keyboard are ordinary and asking the player to choose a mode
 * would be worse than following whatever they just used.
 */

import { useEffect, useState } from 'react';

export type ControlScheme = 'desktop' | 'touch';

function detectInitialScheme(): ControlScheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop';
  }
  return window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'desktop';
}

export function useActiveScheme(): ControlScheme {
  const [scheme, setScheme] = useState<ControlScheme>(detectInitialScheme);

  useEffect(() => {
    const onPointerDown = (event: Event) => {
      const pointerType = (event as PointerEvent).pointerType;
      setScheme(pointerType === 'touch' || pointerType === 'pen' ? 'touch' : 'desktop');
    };
    const onKeyDown = () => setScheme('desktop');

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return scheme;
}
